import { Redirect, useRouter } from 'expo-router';
import { MessageSquare } from 'lucide-react-native';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, StyleSheet } from 'react-native';
import { FadeIn } from '../../src/components/animations';
import { Box, Text, VStack } from '../../src/components/primitives';
import { EmptyState } from '../../src/components/primitives/EmptyState';
import { SearchInput } from '../../src/components/primitives/SearchInput';
import { useAuth } from '../../src/contexts/AuthContext';
import { useTranslation } from '../../src/contexts/TranslationContext';
import { ConversationListItem } from '../../src/features/whatsapp/components/ConversationListItem';
import {
  useWhatsAppConversations,
  useWhatsAppStream,
} from '../../src/features/whatsapp/hooks/useWhatsApp';
import type { WhatsAppConversation } from '../../src/features/whatsapp/types';
import { isWhatsAppEnabled } from '../../src/lib/featureFlags';
import { colors } from '../../src/theme/colors';
import { tokens } from '../../src/theme/tokens';

function Separator() {
  return <Box style={separatorStyle} />;
}

const separatorStyle = { height: 8 };

function ConversationRow({
  item,
  onPress,
  index,
}: {
  item: WhatsAppConversation;
  onPress: (conversation: WhatsAppConversation) => void;
  index: number;
}) {
  const initialDelay = useRef(Math.min(index, 8) * 60).current;
  return (
    <FadeIn delay={initialDelay} duration={400} translateY={12}>
      <ConversationListItem conversation={item} onPress={onPress} />
    </FadeIn>
  );
}

function WhatsAppScreen() {
  const { currentOrganization, getAccessToken } = useAuth();
  const { t } = useTranslation();
  const router = useRouter();

  const orgId = currentOrganization?.id;
  const [search, setSearch] = useState('');

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useWhatsAppConversations(orgId);

  useWhatsAppStream(orgId, getAccessToken);

  const conversations = useMemo(() => {
    const all = data?.pages.flatMap((p) => p.items) ?? [];
    if (!search) return all;
    const q = search.toLowerCase();
    return all.filter(
      (c) =>
        c.lead?.fullName?.toLowerCase().includes(q) ||
        c.waId.includes(q) ||
        c.lead?.phone?.includes(q),
    );
  }, [data, search]);

  const handlePress = useCallback(
    (conversation: WhatsAppConversation) => {
      router.push(`/whatsapp/${conversation.id}`);
    },
    [router],
  );

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const renderItem = useCallback(
    ({ item, index }: { item: WhatsAppConversation; index: number }) => (
      <ConversationRow item={item} onPress={handlePress} index={index} />
    ),
    [handlePress],
  );

  return (
    <Box flex={1}>
      <Box flex={1} backgroundColor={colors.background.primary} padding="lg">
        <VStack spacing="lg" style={styles.container}>
          <FadeIn duration={500}>
            <VStack spacing="xxs">
              <Text variant="h2" color={colors.text.primary}>
                WhatsApp
              </Text>
              <Text variant="caption" color={colors.text.secondary}>
                {t('whatsapp.subtitle') ?? 'Conversations with your leads'}
              </Text>
            </VStack>
          </FadeIn>

          <FadeIn delay={120} duration={500}>
            <SearchInput
              value={search}
              onChangeText={setSearch}
              placeholder="Search conversations..."
            />
          </FadeIn>

          {isLoading ? (
            <Box style={styles.center}>
              <ActivityIndicator size="large" color={colors.primary.main} />
            </Box>
          ) : conversations.length === 0 ? (
            <EmptyState
              icon={<MessageSquare size={48} color={colors.text.tertiary} strokeWidth={1.5} />}
              title="No conversations yet"
              description="WhatsApp conversations will appear here when leads message you."
            />
          ) : (
            <FlatList
              data={conversations}
              keyExtractor={(item) => item.id}
              renderItem={renderItem}
              ItemSeparatorComponent={Separator}
              onEndReached={handleEndReached}
              onEndReachedThreshold={0.3}
              showsVerticalScrollIndicator={false}
              ListFooterComponent={
                isFetchingNextPage ? (
                  <ActivityIndicator color={colors.primary.main} style={styles.footer} />
                ) : null
              }
            />
          )}
        </VStack>
      </Box>
    </Box>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing.xl,
  },
  footer: {
    paddingVertical: tokens.spacing.md,
  },
});

export default function WhatsAppRoute() {
  if (!isWhatsAppEnabled()) {
    return <Redirect href="/dashboard" />;
  }

  return <WhatsAppScreen />;
}
